import { api } from '@/services/Api'

/**
 * Interface pour les données de préremplissage d'un champ
 */
export interface PrefillFieldInfo {
  questionUuid: string
  questionLabel: string
  tagLabel: string
  tagCode: string
}

/**
 * Interface pour le mapping des champs à préremplir
 * Clé = label du tag sans préfixe (ex: "EMAIL", "PHONE")
 * Valeur = informations sur la question correspondante
 */
export interface PrefillFieldsMap {
  [fieldKey: string]: PrefillFieldInfo
}

/**
 * Interface pour les valeurs de préremplissage extraites de l'URL
 * Clé = label du tag (ex: "EMAIL")
 * Valeur = valeur à préremplir (ex: "john@example.com")
 */
export interface PrefillValues {
  [fieldKey: string]: string
}

/**
 * Service de préremplissage des formulaires du comparateur
 *
 * Flux d'utilisation :
 * 1. Extraire les paramètres de l'URL avec extractPrefillValuesFromUrl()
 * 2. Récupérer les champs configurés pour le préremplissage avec getPrefillFields()
 * 3. Stocker les valeurs en session pour les appliquer après création du ResponseGroup
 */
class PrefillService {
  private static instance: PrefillService
  private readonly SESSION_KEY = 'prefill_data'
  private readonly TAG_PREFIX = 'CRM:PREFILL:'

  static getInstance(): PrefillService {
    if (!PrefillService.instance) {
      PrefillService.instance = new PrefillService()
    }
    return PrefillService.instance
  }

  /**
   * Récupère les champs configurés pour le préremplissage d'un formulaire
   * Appelle l'API /question/prefill-fields/{surveySlug}
   */
  async getPrefillFields(surveySlug: string): Promise<PrefillFieldsMap> {
    try {
      const res = await api.get(api.form, `question/prefill-fields/${surveySlug}`)
      if (res && res.data && res.data.status === 'success') {
        return res.data.prefillFields || {}
      }
      return {}
    } catch (error) {
      console.error('Erreur lors de la récupération des champs de préremplissage:', error)
      return {}
    }
  }

  /**
   * Extrait les valeurs de préremplissage depuis les paramètres URL
   * Les paramètres correspondent aux tags CRM:PREFILL:{TYPE}:{FIELD}
   *
   * Exemple URL: ?CONTACT:email=john@example.com&PROP:firstName=John&survey=auto-particulier
   * Retourne: { "CONTACT:email": "john@example.com", "PROP:firstName": "John" }
   */
  extractPrefillValuesFromUrl(): PrefillValues {
    const urlParams = new URLSearchParams(window.location.search)
    const prefillValues: PrefillValues = {}

    // Paramètres systèmes à exclure
    const excludedParams = ['params', 'source', 'affiliation', 'redirect', 'survey']

    urlParams.forEach((value, key) => {
      if (!excludedParams.includes(key.toLowerCase()) && value) {
        // Conserver la clé telle quelle (ex: CONTACT:email, PROP:firstName)
        prefillValues[key] = value
      }
    })

    return prefillValues
  }

  /**
   * Récupère le slug du survey depuis l'URL si présent
   */
  getSurveySlugFromUrl(): string | null {
    const urlParams = new URLSearchParams(window.location.search)
    return urlParams.get('survey')
  }

  /**
   * Vérifie si des valeurs de préremplissage sont présentes dans l'URL
   */
  hasPrefillValuesInUrl(): boolean {
    const values = this.extractPrefillValuesFromUrl()
    return Object.keys(values).length > 0
  }

  /**
   * Stocke les données de préremplissage en session pour utilisation ultérieure
   * Stocke le mapping questionUuid -> valeur
   */
  async storePrefillData(surveySlug: string): Promise<void> {
    const urlValues = this.extractPrefillValuesFromUrl()

    if (Object.keys(urlValues).length === 0) {
      this.clearPrefillData()
      return
    }

    const prefillFields = await this.getPrefillFields(surveySlug)

    // Créer le mapping questionUuid -> valeur
    const prefillData: { [questionUuid: string]: string } = {}

    for (const [fieldKey, value] of Object.entries(urlValues)) {
      const fieldInfo = prefillFields[fieldKey]
      if (fieldInfo && fieldInfo.questionUuid) {
        prefillData[fieldInfo.questionUuid] = value
      }
    }

    if (Object.keys(prefillData).length > 0) {
      sessionStorage.setItem(this.SESSION_KEY, JSON.stringify(prefillData))
    } else {
      this.clearPrefillData()
    }
  }

  /**
   * Récupère les données de préremplissage stockées en session
   * Retourne un mapping questionUuid -> valeur
   */
  getStoredPrefillData(): { [questionUuid: string]: string } {
    const data = sessionStorage.getItem(this.SESSION_KEY)
    if (data) {
      try {
        return JSON.parse(data)
      } catch {
        return {}
      }
    }
    return {}
  }

  /**
   * Vérifie s'il y a des données de préremplissage en session
   */
  hasStoredPrefillData(): boolean {
    const data = this.getStoredPrefillData()
    return Object.keys(data).length > 0
  }

  /**
   * Efface les données de préremplissage de la session
   */
  clearPrefillData(): void {
    sessionStorage.removeItem(this.SESSION_KEY)
  }

  /**
   * Construit une URL avec les paramètres de préremplissage
   * Utile pour générer des liens depuis le CRM
   */
  buildPrefillUrl(baseUrl: string, values: PrefillValues): string {
    const url = new URL(baseUrl, window.location.origin)

    for (const [key, value] of Object.entries(values)) {
      if (value) {
        url.searchParams.set(key.toUpperCase(), value)
      }
    }

    return url.toString()
  }

  /**
   * Génère les paramètres URL à partir des données d'un contact CRM
   * Les clés du contactData doivent correspondre aux labels des tags
   */
  buildPrefillParams(contactData: { [key: string]: string }): string {
    const params = new URLSearchParams()

    for (const [key, value] of Object.entries(contactData)) {
      if (value) {
        params.set(key.toUpperCase(), value)
      }
    }

    return params.toString()
  }
}

export const prefillService = PrefillService.getInstance()
export default prefillService
